import { BaseModel, IModelCourseOptions } from 'models/BaseModel';
import { get, post, postFile } from 'services/request';
import { IRefIds } from '../constants/refs';

export interface ITranslationDashboardExportParams {
  ref: number;
  startLesson: number;
  endLesson: number;
  fields: string[];
  dialects: number[];
}

export interface ITranslationDashboardImportDiff {
  translationDialectId: number;
  dialect: string;
  field: string;
  oldValue: string;
  newValue: string;
}

export interface ITranslationDashboardImportValidateResponse {
  result: string;
  diffs: ITranslationDashboardImportDiff[];
  errors: string[];
}

export interface ITranslationDashboardImportEntry {
  translationDialectId: number;
  text?: string;
  status?: string;
}

export type ITranslationDashboardGrid = {
  id: number;
  ref: number;
  refName: string;
} & {
  [prop: string]: ITranslationDashboardCell;
};

export interface ITranslationDashboardCell {
  pending: number;
  doing: number;
  done: number;
  dialect: number;
}

interface ITranslationDashboard {
  dashboard: ITranslationDashboardGrid[];
}

export default class TranslationDashboardModel extends BaseModel {
  constructor(options: IModelCourseOptions) {
    super(options);
    this.ref = IRefIds.translation;
    this.gridName = 'TranslationDashboard';
    this.getGridColumns();
  }

  createGridColumnsEndpoint() {
    return `grid-columns/${this.gridName}?course=${this.courseId}`;
  }

  async getDashboard(startLesson: number, endLesson: number) {
    this.loadingGrid = true;
    this.render();
    const response = await get<ITranslationDashboard>(`/courses/${this.courseId}/translations/dashboard`, {
      startLesson,
      endLesson,
    });
    this.gridData = response.dashboard;
    this.loadingGrid = false;
    this.render();
  }

  async createProject(data: {
    startLesson: number;
    endLesson: number;
    ref: number;
    dialect: number;
    freelance: number;
  }) {
    this.loadingGrid = true;
    this.render();
    await get(`/courses/${this.courseId}/translations/dashboard/create-project`, data);
    await this.getDashboard(data.startLesson, data.endLesson);
  }

  async exportCSV(params: ITranslationDashboardExportParams) {
    const response = await get<{ fileData: string; fileName: string }>(
      `/courses/${this.courseId}/translations/dashboard/export`,
      {
        ref: params.ref,
        startLesson: params.startLesson,
        endLesson: params.endLesson,
        fields: params.fields.join(','),
        dialects: params.dialects.join(','),
      },
    );

    if (response.fileData) {
      const blob = new Blob([response.fileData], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = response.fileName || 'translations.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
  }

  async validateImportCSV(ref: number, file: File): Promise<ITranslationDashboardImportValidateResponse> {
    const response = await postFile<ITranslationDashboardImportValidateResponse>(
      `/courses/${this.courseId}/translations/dashboard/import/validate?ref=${ref}`,
      file,
    );

    return response.data || { result: 'ERROR', diffs: [], errors: ['Failed to validate file'] };
  }

  async executeImport(entries: ITranslationDashboardImportEntry[]): Promise<{ result: string; updated: number; errors: string[] }> {
    const response = await post<{ result: string; updated: number; errors: string[] }>(
      `/courses/${this.courseId}/translations/dashboard/import/execute`,
      entries,
    );

    return response.data || { result: 'ERROR', updated: 0, errors: ['Failed to execute import'] };
  }
}
